import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismLoadingSpinner,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type { Currency } from 'lumen/models';
import type CurrencyConversionOrganizationContext from 'lumen/models/currency-conversion/organization-context';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface SettingsCurrencyConversionDefaultCurrencyPickerSignature {
  Args: {
    isLoading: boolean;
    isReadOnly?: boolean;
    portfolioCurrencies: Currency[];
  };
  Element: HTMLDivElement;
}

type CurrencyOption = {
  currency: Currency;
  id: string;
  label: string;
};

export default class SettingsCurrencyConversionDefaultCurrencyPicker extends Component<SettingsCurrencyConversionDefaultCurrencyPickerSignature> {
  @service declare alerting: Services['alerting'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked private pendingCurrency: Currency | null = null;

  fetchAllCurrenciesTask = task(
    { restartable: true },
    async (): Promise<Currency[]> => {
      const userId = this.hotelAndMarketService.user.id;

      try {
        return await this.store.query<Currency>('currency', {
          namespace: APIGATEWAY,
          typeKey: `users/${userId}/currencies`,
        });
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchOrganizationContextTask = task(
    { restartable: true },
    async (): Promise<CurrencyConversionOrganizationContext | null> => {
      try {
        return await this.currencyConversion.getOrganizationContextTask.perform();
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get allCurrencies(): Currency[] {
    return this.fetchAllCurrenciesTask.last?.value ?? [];
  }

  get defaultCurrency(): Currency | null {
    const code = this.organizationContext?.defaultCurrency;
    if (!code) {
      return null;
    }
    return this.allCurrencies.find((c) => c.code === code) ?? null;
  }

  get displayCurrency(): Currency | null {
    return this.pendingCurrency ?? this.effectiveDefault;
  }

  get effectiveDefault(): Currency | null {
    return this.defaultCurrency ?? this.args.portfolioCurrencies[0] ?? null;
  }

  @cached
  get groupedOptions(): { groupName: string; options: CurrencyOption[] }[] {
    const toOption = (c: Currency): CurrencyOption => ({
      currency: c,
      id: c.code,
      label: `${c.name} (${c.symbol})`,
    });

    return [
      {
        groupName: this.intl.t(
          'settings.currency_conversion.default_currency.portfolio_group',
        ),
        options: this.args.portfolioCurrencies.map(toOption),
      },
      {
        groupName: this.intl.t('general.all'),
        options: this.nonPortfolioCurrencies.map(toOption),
      },
    ];
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.pendingCurrency !== null &&
      this.pendingCurrency.code !== this.effectiveDefault?.code
    );
  }

  get isApplying(): boolean {
    return this.currencyConversion.updateDefaultCurrencyTask.isRunning;
  }

  get nonPortfolioCurrencies(): Currency[] {
    const portfolioCurrencyCodesSet = new Set(
      this.args.portfolioCurrencies.map((c) => c.code),
    );
    return this.allCurrencies.filter(
      (c) => !portfolioCurrencyCodesSet.has(c.code),
    );
  }

  get organizationContext(): CurrencyConversionOrganizationContext | null {
    return this.fetchOrganizationContextTask.last?.value ?? null;
  }

  get selectedOption(): CurrencyOption | undefined {
    return this.groupedOptions
      .flatMap((group) => group.options)
      .find((option) => option.id === this.displayCurrency?.code);
  }

  get showLoadingState(): boolean {
    return (
      !this.selectedOption ||
      this.fetchAllCurrenciesTask.isRunning ||
      this.fetchOrganizationContextTask.isRunning ||
      this.args.isLoading
    );
  }

  constructor(
    owner: unknown,
    args: SettingsCurrencyConversionDefaultCurrencyPickerSignature['Args'],
  ) {
    // @ts-expect-error: Owner type mismatch between unknown and Glimmer Owner
    super(owner, args);
    void this.fetchAllCurrenciesTask.perform();
    void this.fetchOrganizationContextTask.perform();
  }

  searchMatcher(option: unknown, searchText: string): number {
    const opt = option as { id?: string; label?: string };
    const q = searchText.toLowerCase();
    const matches =
      opt.label?.toLowerCase().includes(q) || opt.id?.toLowerCase().includes(q);
    return matches ? 1 : -1;
  }

  @action
  async handleApply(): Promise<void> {
    if (!this.pendingCurrency || !this.organizationContext) {
      return;
    }
    this.organizationContext.defaultCurrency = this.pendingCurrency.code;
    try {
      await this.currencyConversion.updateDefaultCurrencyTask.perform(
        this.organizationContext,
      );

      this.usageAnalytics.track('Default Currency Changed', {
        eventTrigger: 'default-currency-change',
      });
    } finally {
      this.pendingCurrency = null;
    }
  }

  @action
  handleSelect(option: CurrencyOption): void {
    this.pendingCurrency = option.currency;
  }

  <template>
    <PrismCard>
      <:header as |Header|>
        <Header.title>
          {{t "settings.currency_conversion.default_currency.label"}}
        </Header.title>
      </:header>

      <:content>
        {{#if @isReadOnly}}
          {{#if this.showLoadingState}}
            <PrismLoadingSpinner />
          {{else}}
            {{#if this.effectiveDefault}}
              <p class="text-300--regular">
                {{this.effectiveDefault.name}}
                ({{this.effectiveDefault.symbol}})
              </p>
            {{/if}}
          {{/if}}
          <p class="text-300--regular text-neutral-subdued mt-200">
            {{t "settings.currency_conversion.default_currency.description"}}
          </p>
        {{else}}
          <div class="flex gap-200">
            {{#if this.showLoadingState}}
              <PrismLoadingSpinner />
            {{else}}
              <PrismComboSelectInput
                @disabled={{this.isApplying}}
                @isSearchable={{true}}
                @onChange={{this.handleSelect}}
                @options={{this.groupedOptions}}
                @searchMatcher={{this.searchMatcher}}
                @unsafe_hasDropdownMaxHeight={{true}}
                @unsafe_matchTriggerWidth={{false}}
                @value={{this.selectedOption}}
              />
            {{/if}}

            {{#if (or this.hasUnsavedChanges this.isApplying)}}
              <PrismButton
                @isLoading={{this.isApplying}}
                @label={{t "general.apply"}}
                @onClick={{this.handleApply}}
              />
            {{/if}}
          </div>

          <p class="text-300--regular text-neutral-subdued mt-200">
            {{t "settings.currency_conversion.default_currency.description"}}
          </p>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
